import { Option, Select, Text, TextField } from '@shared/components';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { getUserFromMe } from '@shared/contexts/MeContext';
import { useAuthorizedIntegrationContext } from '@shared/contexts/pluginContexts/AuthorizedIntegrationContext';
import { useEmailThreadContext } from '@shared/contexts/pluginContexts/EmailThreadContext';
import { UserFeaturesDisplay } from '@shared/features/user-features-display';
import UserFeaturesFormContainer from '@shared/features/user-features-form/UserFeaturesForm';
import { getInitialEmailScanningFormData } from '@shared/features/user-features-form/utils/featuresForm';
import { useIsChatsLandingAvailable } from '@shared/plugin/pages/Landing';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { useCurrentClientVersion } from '@shared/utils/version';
import { uniq } from 'lodash';
import { useState } from 'react';
import { LandingPageSelect } from './LandingPageSelect';

const ROOT = makeRootClassName('AccountSettings');
export const el = makeElementClassNameFactory(ROOT);

const NOT_SHARED_DISPLAY_TEXT = 'Not a shared mailbox';

interface SharedInboxProps {
  title: string;
  children: React.ReactNode;
}

const Section = ({ title, children }: SharedInboxProps) => {
  return (
    <div className={el`section`}>
      <Text className={el`section-title`} type="body-md" isHeavy>
        {title}
      </Text>
      {children}
    </div>
  );
};

const AccountRow = ({ email, type }: { email: string; type: string }) => {
  return (
    <div className="flex w-full cursor-not-allowed flex-row gap-1 text-gray-700">
      <TextField
        className="w-full"
        size="small"
        label={type}
        value={email}
        isDisabled
      />
    </div>
  );
};

const AccountSettings = () => {
  const { sharedInbox } = useEmailThreadContext();
  const {
    address: sharedMailboxAddress,
    candidates,
    selectCandidate,
  } = sharedInbox;
  const { authorized, authorizedEmail, integrationName } =
    useAuthorizedIntegrationContext();
  const [isEditingFeatures, setEditingFeatures] = useState(false);
  const { me, defaultOrgId } = useMeContext();
  const clientVersion = useCurrentClientVersion();
  const { isAvailable: isChatsLandingAvailable } = useIsChatsLandingAvailable();

  const authItems = [
    <AccountRow
      key="account-row-vooma"
      email={me?.email ?? 'Unknown'}
      type="Vooma"
    />,
  ];

  if (authorized) {
    authItems.push(
      <AccountRow
        key="account-row-integration"
        email={authorizedEmail ?? 'Unknown'}
        type={integrationName}
      />
    );
  }

  const sharedMailboxOptions = uniq([
    '',
    ...(sharedMailboxAddress ? [sharedMailboxAddress] : []),
    ...candidates,
  ]);

  const user = getUserFromMe(me);
  const initialEmailScanningData = getInitialEmailScanningFormData(
    defaultOrgId ?? undefined,
    user
  );
  const showFeatures = user && defaultOrgId && initialEmailScanningData.enabled;

  return (
    <div className={ROOT}>
      <Text className={el`page-title`} type="body-md" isHeavy>
        Account settings
      </Text>

      <Section title={'Shared mailbox'}>
        <Select
          placeholder="Select shared mailbox..."
          className={el`select-shared-inbox`}
          value={sharedMailboxAddress}
          renderValue={(val) => val ?? NOT_SHARED_DISPLAY_TEXT}
          onChange={(val) => selectCandidate(val)}
        >
          {sharedMailboxOptions.map((email) => (
            <Option key={email ?? 'not-shared'} value={email}>
              {email || NOT_SHARED_DISPLAY_TEXT}
            </Option>
          ))}
        </Select>
        <Text type="body-xs" className={el`shared-inbox-details`}>
          {`If you are accessing messages in a shared mailbox, select it from the
          list above. Alternatively, select '${NOT_SHARED_DISPLAY_TEXT}' if you
          are using an individual inbox or merely accessing messages from a
          distribution list of which you are a member.`}
        </Text>
      </Section>

      {isChatsLandingAvailable && (
        <Section title={'Start page'}>
          <LandingPageSelect className={el`select-landing-page`} />
          <Text type="body-xs" className={el`landing-page-details`}>
            The page Vooma opens on when you open the plugin.
          </Text>
        </Section>
      )}

      {showFeatures && (
        <>
          {isEditingFeatures ? (
            <UserFeaturesFormContainer
              onDiscard={() => setEditingFeatures(false)}
              user={user}
              organizationId={defaultOrgId}
              className="w-full bg-gray-50 p-2"
            />
          ) : (
            <UserFeaturesDisplay
              onEdit={() => setEditingFeatures(true)}
              user={user}
              className="w-full bg-gray-50 p-2"
            />
          )}
        </>
      )}

      <Section title={'User'}>
        <div className="mt-2 flex w-full flex-col gap-2">{authItems}</div>
      </Section>

      <Section title={'Version'}>
        <div className="mt-2 flex w-full flex-col gap-2 text-gray-400">
          <Text type="body-xs">{clientVersion ?? 'Unavailable'}</Text>
        </div>
      </Section>
    </div>
  );
};

export default AccountSettings;
